/**
 * Weapon Lab input — training mode pause menu with tank/shot/wind/dummy controls.
 */

import { Keys } from 'bonkjs';
import type { InputProvider } from '../../input';
import type { GameState } from '../../types';
import type { GameEffect } from '../effects';
import { transitionPhase } from '../effects';
import { resetGame } from '../reset';
import { PHYSICS } from '../../constants';
import { justPressed } from '../input-edge';
import { getAllTankIds, getTankShot, getEffectiveShots } from '../../tanks';
import { DEFAULT_STAGE_ID } from '../../stages';
import { getGroundY } from '../../terrain/grid';
import { mirrorSettle } from '../../physics/transitions';
import { tankColor } from './helpers';

/** Lab menu rows: TANK, SHOT_TIER, WIND, DUMMY_DAMAGE, RESET_DUMMY, RESET_TERRAIN, RESUME, MAIN_MENU */
const LAB_ROW_COUNT = 8;
const LAB_ROW_TANK = 0;
const LAB_ROW_SHOT_TIER = 1;
const LAB_ROW_WIND = 2;
const LAB_ROW_DUMMY_DAMAGE = 3;
const LAB_ROW_RESET_DUMMY = 4;
const LAB_ROW_RESET_TERRAIN = 5;
const LAB_ROW_RESUME = 6;
const LAB_ROW_MAIN_MENU = 7;

export function handleWeaponLabInput(state: GameState, input: InputProvider, fx: GameEffect[]): void {
  const ms = state.modeState;
  const player = state.players[0];
  const tankIds = getAllTankIds();

  // Navigate rows
  if (justPressed(Keys.ArrowUp, input) || justPressed(Keys.W, input)) {
    ms.labCursor = Math.max(0, ms.labCursor - 1);
  }
  if (justPressed(Keys.ArrowDown, input) || justPressed(Keys.S, input)) {
    ms.labCursor = Math.min(LAB_ROW_COUNT - 1, ms.labCursor + 1);
  }

  const leftPressed = justPressed(Keys.ArrowLeft, input) || justPressed(Keys.A, input);
  const rightPressed = justPressed(Keys.ArrowRight, input) || justPressed(Keys.D, input);
  const confirmPressed = justPressed(Keys.Space, input) || justPressed(Keys.Enter, input);

  switch (ms.labCursor) {
    case LAB_ROW_TANK: {
      if (leftPressed || rightPressed) {
        const dir = rightPressed ? 1 : -1;
        const curIdx = tankIds.indexOf(player.tankId);
        const nextIdx = (curIdx + dir + tankIds.length) % tankIds.length;
        player.tankId = tankIds[nextIdx];
        player.color = tankColor(player.tankId);
        player.selectedShotTier = 1;
        player.selectedWeapon = getTankShot(player.tankId, 1, player.terraformerMode).weapon;
      }
      break;
    }
    case LAB_ROW_SHOT_TIER: {
      const labShots = getEffectiveShots(player.tankId, player.terraformerMode);
      if (leftPressed) player.selectedShotTier = Math.max(1, player.selectedShotTier - 1);
      if (rightPressed) player.selectedShotTier = Math.min(labShots.length, player.selectedShotTier + 1);
      if (leftPressed || rightPressed) {
        player.selectedWeapon = getTankShot(player.tankId, player.selectedShotTier, player.terraformerMode).weapon;
      }
      break;
    }
    case LAB_ROW_WIND: {
      const step = 0.01;
      if (leftPressed) {
        ms.labWindOverride = Math.max(-0.20, (ms.labWindOverride ?? state.wind) - step);
      }
      if (rightPressed) {
        ms.labWindOverride = Math.min(0.20, (ms.labWindOverride ?? state.wind) + step);
      }
      // Space toggles between calm(0) and off(null)
      if (confirmPressed) {
        if (ms.labWindOverride === null) {
          ms.labWindOverride = 0;
        } else {
          ms.labWindOverride = null;
        }
      }
      break;
    }
    case LAB_ROW_DUMMY_DAMAGE: {
      if (leftPressed) ms.labDummyDamage = Math.max(0, ms.labDummyDamage - 10);
      if (rightPressed) ms.labDummyDamage = Math.min(300, ms.labDummyDamage + 10);
      break;
    }
    case LAB_ROW_RESET_DUMMY: {
      if (confirmPressed) {
        const dummy = state.players[1];
        if (dummy) {
          const centerX = (state.stageLeft + state.stageRight) / 2;
          dummy.x = centerX + 200;
          dummy.y = getGroundY(dummy.x) - PHYSICS.ECB_RADIUS;
          dummy.vx = 0;
          dummy.vy = 0;
          dummy.tumble = false;
          dummy.hitstunFrames = 0;
          dummy.settled = true;
          dummy.settledX = dummy.x;
          dummy.settledY = dummy.y;
          mirrorSettle(dummy);
          dummy.damagePercent = ms.labDummyDamage;
          dummy.alive = true;
          dummy.pendingDeath = false;
          ms.labLastHit = null;
        }
      }
      break;
    }
    case LAB_ROW_RESET_TERRAIN: {
      if (confirmPressed) {
        fx.push(...resetGame(state));
        state.paused = false;
        ms.labLastHit = null;
      }
      break;
    }
    case LAB_ROW_RESUME: {
      if (confirmPressed) {
        state.paused = false;
      }
      break;
    }
    case LAB_ROW_MAIN_MENU: {
      if (confirmPressed) {
        state.paused = false;
        transitionPhase(state, 'title_screen', fx);
        fx.push({ type: 'stage_theme_change', stageId: DEFAULT_STAGE_ID });
      }
      break;
    }
  }

  // ESC always resumes
  if (justPressed(Keys.Escape, input)) {
    state.paused = false;
  }
}
